import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Alert,
  useTheme,
  Typography,
  Paper,
  Link,
  Autocomplete,
  Chip,
  Box,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { CREATE_AUDIT } from "../../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_FRAMEWORK_LIST } from "../../../../../apis/settingsApis/framework-apis/frameworkApis";
import { GET_EXTERNAL_USERS } from "../../../../../apis/organization-apis/people-apis/externalUserApis";
import { uploadFile } from "../../../../../apis/common-apis/fileApi";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_COMPANY_NOTIFICATION_PREFERENCES } from "../../../../../apis/settingsApis/notifications/notificationApis";
import { LoadingButton } from "@mui/lab";
import { useAuditForm } from "../../hooks/useAuditForm";
import { isAuditPlanRequired } from "../../utils/auditFormValidation";
import FrameworkSelector from "../common/FrameworkSelector";
import EvidenceWindowSelector from "./EvidenceWindowSelector";
import {
  AuditStatus,
  NotificationTemplate,
  SOC2FrameworkType,
} from "../../../../../utils/enums";
import DatePicker from "../../../../../components/DatePicker/DatePicker";
import { getStartOfDayUTC, getEndOfDayUTC } from "../../../../../utils/dateUtils";
import { firstActiveAuditorRowPerId } from "../../utils/auditDetailsUtils";

const AUDIT_PLAN_ALLOWED_TYPES = [".pdf", ".docx"];
const CreateAuditDialog = ({ open, onClose, setSnackbarInfo, refetchAudits }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    calculateEndDate,
    handleChange,
    handleEvidenceWindowChange,
    validateForm,
    resetForm,
  } = useAuditForm();

  const [auditPlanError, setAuditPlanError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auditPlanInputRef = useRef(null);

  const [createAudit, { loading: isCreatingAudit }] = useMutation(CREATE_AUDIT);
  const isCreating = isSubmitting || isCreatingAudit;

  const addAuditPlanFiles = (incoming) => {
    const incomingList = Array.from(incoming || []).filter(Boolean);
    if (incomingList.length === 0) return;

    const valid = [];
    let hadInvalid = false;
    for (const file of incomingList) {
      const isAllowed = AUDIT_PLAN_ALLOWED_TYPES.some((ext) =>
        file.name?.toLowerCase().endsWith(ext)
      );
      if (isAllowed) {
        valid.push(file);
      } else {
        hadInvalid = true;
      }
    }

    setAuditPlanError(hadInvalid);
    if (valid.length === 0) return;

    setFormData((prev) => {
      const existing = prev.audit_plan_files || [];
      // Avoid duplicates by name+size+lastModified
      const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
      const existingKeys = new Set(existing.map(key));
      const deduped = valid.filter((f) => !existingKeys.has(key(f)));
      return { ...prev, audit_plan_files: [...existing, ...deduped] };
    });

    if (errors.audit_plan_files) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.audit_plan_files;
        return next;
      });
    }
  };

  const removeAuditPlanFileAt = (idx) => {
    setFormData((prev) => {
      const list = prev.audit_plan_files || [];
      return { ...prev, audit_plan_files: list.filter((_, i) => i !== idx) };
    });
    if (auditPlanInputRef.current) {
      auditPlanInputRef.current.value = "";
    }
  };
  
  const { data: frameworksData } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "cache-first",
  });

  const { data: externalUsersData } = useQuery(GET_EXTERNAL_USERS, {
    variables: { page: 1, limit: 100 },
    fetchPolicy: "cache-and-network",
  });

  const { data: employeesData } = useQuery(GET_STAFF_DETAILS, {
    variables: { filter: { in_scope: true } },
    fetchPolicy: "cache-and-network",
  });

  // Only fetched while the dialog is open, and skipped otherwise so closing it
  // does not keep polling notification settings.
  const { data: notificationPrefsData } = useQuery(
    GET_COMPANY_NOTIFICATION_PREFERENCES,
    { fetchPolicy: "cache-and-network", skip: !open }
  );

  // Undefined until the query resolves, so the alert stays hidden rather than
  // flashing on for a moment while it loads.
  const isCheckRegressionOff = useMemo(() => {
    const rows = notificationPrefsData?.getCompanyNotificationPreferences;
    if (!rows) return false;
    const checkRegression = rows.filter(
      (row) => row.template_key === NotificationTemplate.CHECK_REGRESSION
    );
    if (!checkRegression.length) return false;
    return checkRegression.every((row) => !row.enabled);
  }, [notificationPrefsData]);

  const frameworks = useMemo(() => {
    const allFrameworks = frameworksData?.getFramework || [];
    return allFrameworks.filter(f => f.is_active === true);
  }, [frameworksData]);

  const selectedFramework = useMemo(() => {
    const id = formData.soc2_standard_id ?? formData.framework_id;
    return id ? frameworks.find(f => f.framework_id === id) : null;
  }, [frameworks, formData.framework_id, formData.soc2_standard_id]);

  const auditPlanRequired = useMemo(
    () => isAuditPlanRequired(formData),
    [formData.framework_key, formData.audit_type]
  );

  const isPointInTimeAudit = useMemo(() => {
    if (formData.framework_base === "SOC2" && formData.framework_type === "type1") {
      return true;
    }
    if (!selectedFramework) return false;
    return selectedFramework.maximum_evidence_window_size === 1;
  }, [selectedFramework, formData.framework_base, formData.framework_type]);

  // External auditor options (is_internal: false)
  const externalAuditorOptions = useMemo(() =>
    firstActiveAuditorRowPerId(externalUsersData?.getExternalUsers?.data || []).map((u) => ({
      id: String(u.id),
      name: u.name || "",
      email: u.email || "",
      is_internal: false,
    })),
    [externalUsersData]
  );

  // Internal employee options (is_internal: true) — only in-scope, non-terminated
  const internalEmployeeOptions = useMemo(() =>
    (employeesData?.getEmployees?.data || [])
      .filter((emp) => emp.employee_data_id && emp.in_scope !== false && !emp.termination_date)
      .map((emp) => ({
        id: String(emp.employee_data_id),
        name: emp.name || emp.work_email?.split("@")[0] || "",
        email: emp.work_email || "",
        is_internal: true,
      })),
    [employeesData]
  );

  // For external audits: only external auditors; for internal: combined
  const auditorOptions = useMemo(() => {
    if (formData.audit_type === "external") return externalAuditorOptions;
    return [...internalEmployeeOptions, ...externalAuditorOptions];
  }, [formData.audit_type, internalEmployeeOptions, externalAuditorOptions]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  // Clear auditors that violate external-only rule when audit_type changes
  useEffect(() => {
    if (formData.audit_type === "external" && formData.auditors?.some((a) => a.is_internal)) {
      setFormData((prev) => ({
        ...prev,
        auditors: prev.auditors.filter((a) => !a.is_internal),
      }));
    }
  }, [formData.audit_type]);

  const audit_end_date = calculateEndDate();

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      let endDate = calculateEndDate();

      if (isPointInTimeAudit) {
        endDate = formData.audit_start_date;
      }

      let frameworkId;
      if (formData.framework_base === "SOC2") {
        frameworkId = formData.soc2_standard_id;
      } else {
        frameworkId = formData.framework_id;
      }

      const input = {
        audit_name: formData.audit_name.trim(),
        framework_id: parseInt(frameworkId),
        audit_type: formData.audit_type,
        audit_status: AuditStatus.ACTIVE,
        audit_start_date: getStartOfDayUTC(formData.audit_start_date),
        audit_end_date: getEndOfDayUTC(endDate),
        auditors: formData.auditors.map((a) => ({ id: String(a.id), is_internal: a.is_internal })),
      };

      const result = await createAudit({
        variables: { input },
      });

      const newAuditId = result.data?.createAudit?.data?.[0]?.id;
      const selectedPlanFiles = formData.audit_plan_files || [];
      if (result.data?.createAudit?.success && newAuditId && selectedPlanFiles.length > 0) {
        try {
          const accessToken = await getAccessTokenSilently();
          const idToken = (await getIdTokenClaims()).__raw;
          await Promise.all(
            selectedPlanFiles.map((file) =>
              uploadFile(
                file,
                "audit_document",
                newAuditId,
                undefined,
                accessToken,
                idToken,
                null,
                true
              )
            )
          );
        } catch (uploadErr) {
          console.error("Audit plan upload failed after audit creation:", uploadErr);
          setSnackbarInfo({
            message:
              "Audit created, but some audit-plan files failed to upload. " +
              "You can re-upload them from the Audit Plan tab.",
            vertical: "top",
            horizontal: "right",
          });
          refetchAudits();
          onClose();
          return;
        }
      }

      if (result.data?.createAudit?.success) {
        setSnackbarInfo({
          message: "Audit created successfully!",
          vertical: "top",
          horizontal: "right",
        });
        refetchAudits();
        onClose();
      } else {
        setSnackbarInfo({
          message: result.data?.createAudit?.message || "Failed to create audit",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error creating audit:", error);
      setSnackbarInfo({
        message: error.message || "An error occurred while creating the audit",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxWidth: "700px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 2,
          pt: 2.5,
          px: 3,
          position: "relative",
        }}
      >
        Create New Audit
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: theme.customColors.text.secondary,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 4,
          py: 4,
          "&.MuiDialogContent-root": {
            paddingTop: 3.5,
          },
        }}
      >
        {/* Only shown while the alert is off, so it disappears once acted on. */}
        {isCheckRegressionOff && (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Check regression alerts are turned off. Turn them on to be emailed
            whenever a check in controls assigned to you starts failing.{" "}
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/company-settings?tab=notifications")}
              sx={{ fontWeight: 600 }}
            >
              Notification settings
            </Link>
          </Alert>
        )}

        <Box sx={{ mb: 0.5 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Audit Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., SOC 2 Type 2 - Jan 2026 to Dec 2026"
            value={formData.audit_name}
            onChange={handleChange("audit_name")}
            disabled={isCreating}
            error={!!errors.audit_name}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "&:hover fieldset": {
                  borderColor: theme.customColors.border.strong,
                },
              },
            }}
          />
          {errors.audit_name && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.audit_name}
            </Alert>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            mb: 2.5,
            fontSize: "0.8125rem",
            color: theme.customColors?.text?.secondary ?? "#6B7280",
          }}
        >
          Format: Framework + Type + Period (e.g., HIPAA Audit - 2026-01 to 2026-12)
        </Typography>

        <FrameworkSelector
          frameworks={frameworks}
          formData={formData}
          errors={errors}
          creating={isCreating}
          setFormData={setFormData}
          setErrors={setErrors}
        />

        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Audit Type *
          </Typography>
          <FormControl fullWidth error={!!errors.audit_type} size="small">
            <Select
              value={formData.audit_type}
              onChange={handleChange("audit_type")}
              disabled={isCreating}
              displayEmpty
              sx={{
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.customColors.border.strong,
                },
              }}
            >
              <MenuItem value="" disabled>Select audit type</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="external">External</MenuItem>
            </Select>
          </FormControl>
          {errors.audit_type && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.audit_type}
            </Alert>
          )}
        </Box>

        <Box sx={{ mb: 0.5 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Auditors *
          </Typography>
          {formData.audit_type === "external" && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: theme.customColors?.text?.secondary }}
            >
              External audits can only have external auditors assigned.
            </Typography>
          )}
          <Autocomplete
            multiple
            options={auditorOptions}
            value={formData.auditors || []}
            onChange={(_, value) => {
              setFormData((prev) => ({ ...prev, auditors: value }));
              if (errors.auditors) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.auditors;
                  return newErrors;
                });
              }
            }}
            disabled={isCreating}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id) && option.is_internal === value.is_internal
            }
            groupBy={(option) => (option.is_internal ? "Internal Employees" : "External Auditors")}
            renderOption={(props, option) => {
              const { key, ...liProps } = props;
              return (
                <li key={key} {...liProps} style={{ padding: "8px 16px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                      gap: 0.25,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: theme.customColors.text.primary, lineHeight: 1.3 }}
                      >
                        {option.name}
                      </Typography>
                      <Chip
                        label={option.is_internal ? "Internal" : "External"}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          bgcolor: option.is_internal ? "#EEF2FF" : "#F0FDF4",
                          color: option.is_internal ? "#4F46E5" : "#16A34A",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.customColors.text.secondary, lineHeight: 1.3 }}
                    >
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option.id}-${option.is_internal}`}
                  label={option.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${option.is_internal ? "#A5B4FC" : theme.customColors?.border?.main ?? "#E8EAED"}`,
                    backgroundColor: option.is_internal ? "#EEF2FF" : (theme.customColors?.background?.main ?? "#FFFFFF"),
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  !formData.audit_type
                    ? "Select audit type first"
                    : formData.auditors?.length > 0
                    ? ""
                    : auditorOptions.length === 0
                    ? "No auditors available"
                    : "Search and select auditors"
                }
                variant="outlined"
                size="small"
                error={!!errors.auditors}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            )}
            noOptionsText="No auditors available"
          />
          {errors.auditors && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.auditors}
            </Alert>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            mb: 2.5,
            fontSize: "0.8125rem",
            color: theme.customColors?.text?.secondary ?? "#6B7280",
          }}
        >
          Need to add an auditor?{" "}
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/people?tab=external-users");
            }}
            sx={{
              fontSize: "0.8125rem",
              color: theme.palette.primary.main,
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Create external user here.
          </Link>
        </Typography>

        {/* Audit Plan — required for internal ISO/IEC 27001:2022 (matches backend validation).
            Multiple files supported; each is uploaded individually on submit. */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Audit Plan {auditPlanRequired ? "*" : "(Optional)"}
          </Typography>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addAuditPlanFiles(e.dataTransfer?.files);
            }}
            onClick={() => auditPlanInputRef.current && auditPlanInputRef.current.click()}
            sx={{
              border: "1px dashed",
              borderColor:
                auditPlanError || errors.audit_plan_files ? "#C71414" : "#031B89",
              borderRadius: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: isCreating ? "not-allowed" : "pointer",
              opacity: isCreating ? 0.6 : 1,
              backgroundColor: "#FFFFFF",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 36, mb: 0.5, color: "#031B89" }} />
            <Typography
              variant="body2"
              sx={{ color: auditPlanError ? "#C71414" : "#031B89", fontWeight: 500 }}
            >
              Drag and drop files here, or click to browse
            </Typography>
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: "text.secondary", textAlign: "center" }}
            >
              Allowed file types: {AUDIT_PLAN_ALLOWED_TYPES.join(", ")}
            </Typography>
            <input
              type="file"
              accept={AUDIT_PLAN_ALLOWED_TYPES.join(",")}
              hidden
              multiple
              ref={auditPlanInputRef}
              onChange={(e) => addAuditPlanFiles(e.target.files)}
              disabled={isCreating}
            />
          </Box>

          {(formData.audit_plan_files || []).length > 0 && (
            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {formData.audit_plan_files.map((file, idx) => (
                <Box
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                    py: 0.5,
                    border: "1px solid #E5E7EB",
                    borderRadius: 1,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <AttachFileIcon sx={{ color: "#031B89", mr: 1, fontSize: 18 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#031B89",
                      mr: 1,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <IconButton
                    onClick={() => removeAuditPlanFileAt(idx)}
                    size="small"
                    disabled={isCreating}
                  >
                    <DeleteIcon fontSize="small" sx={{ color: "#031B89" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {auditPlanError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Please select valid files ({AUDIT_PLAN_ALLOWED_TYPES.join(", ")}).
            </Alert>
          )}
          {errors.audit_plan_files && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.audit_plan_files}
            </Alert>
          )}
        </Box>

        {/* Conditional Date Fields based on audit type */}
        {isPointInTimeAudit ? (
          <DatePicker
            fullWidth
            label="Audit Date"
            required
            value={formData.audit_start_date}
            onChange={(date) => handleChange("audit_start_date")({ target: { value: date } })}
            disabled={isCreating}
            error={!!errors.audit_start_date}
            helperText={errors.audit_start_date || "Select the date for this point-in-time audit"}
            size="small"
            sx={{ mb: 2.5 }}
          />
        ) : (
          <>
        <DatePicker
          fullWidth
          label="Audit Start Date"
          required
          value={formData.audit_start_date}
          onChange={(date) => handleChange("audit_start_date")({ target: { value: date } })}
          disabled={isCreating}
          error={!!errors.audit_start_date}
          helperText={errors.audit_start_date}
          size="small"
          sx={{ mb: 3 }}
        />

        <EvidenceWindowSelector
          formData={formData}
          errors={errors}
          audit_end_date={audit_end_date}
          creating={isCreating}
          handleChange={handleChange}
          handleEvidenceWindowChange={handleEvidenceWindowChange}
        />

        <DatePicker
          fullWidth
          label="Audit End Date"
          required
          value={formData.audit_window_months === "custom" ? formData.custom_end_date : audit_end_date}
          onChange={(date) => handleChange("custom_end_date")({ target: { value: date } })}
          disabled={isCreating || formData.audit_window_months !== "custom"}
          error={!!errors.custom_end_date}
          helperText={errors.custom_end_date}
          size="small"
          sx={{ mb: 3 }}
        />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 2.5,
          pt: 2,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={isCreating}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            bgcolor: theme.customColors.button.hover,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.customColors.button.primary,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          Create Audit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAuditDialog;
